import { cn } from '@src/lib/utils';

import { useApiBaseUrl } from '@shared/hooks/useApiBaseUrl';
import { useQueryFetch } from '@shared/hooks/useQueryFetch';
import ProSpinner from '@shared/newComponents/ProSpinner';
import { getFileMetaText } from '@shared/util/fileMeta';

import { FileCardFlow } from '../../FileCardFlow';
import type { FileContent } from '../../FilePreview';

import { ShadcnButton, ShadcnModal } from '@shadcn-features/core';
import { Download, Eye, ImageIcon, RotateCw, Trash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    id: string;
    readOnly?: boolean;
    onDelete: (id: string) => void;
};

export function ProcessFieldPhotoPreview({ id, onDelete, readOnly }: Props) {
    const { t } = useTranslation();
    const apiBaseUrl = useApiBaseUrl();
    const [isImageError, setIsImageError] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [requestVersion, setRequestVersion] = useState(0);
    const photo = useQueryFetch<FileContent & { size?: number }>(
        `flow/Files/GetBase64/${id}`,
        { enabled: !!id, hideErrorNotification: true },
    );
    const imageUrl = photo.data?.content
        ? `data:${photo.data.extension};base64,${photo.data.content}`
        : photo.isFetching
          ? undefined
          : `${apiBaseUrl}flow/Files/Download/${id}`;
    const handleImageError = () => {
        setIsImageError(true);
        setIsLoading(false);
    };
    const file = {
        id,
        name: photo.data?.name || t('translation:photo'),
        size: photo.data?.size,
    };
    const metaText = getFileMetaText(file);
    const fileDetails = (
        <div className='flex min-w-0 flex-1 flex-col gap-0.5 break-words'>
            <div
                className='truncate text-sm leading-normal font-medium text-(--color-card-text)'
                title={file.name}
            >
                {file.name}
            </div>
            {metaText && (
                <div
                    className='truncate text-[11px] leading-normal font-normal text-(--color-card-subtitle)'
                    title={metaText}
                >
                    {metaText}
                </div>
            )}
        </div>
    );

    const download = async () => {
        const data = photo.data ?? (await photo.refetch()).data;
        if (!data?.content) return;

        const bytes = atob(data.content);
        const array = Uint8Array.from(bytes, (character) =>
            character.charCodeAt(0),
        );
        const url = URL.createObjectURL(
            new Blob([array], { type: data.extension }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.download = data.name || file.name;
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <ProSpinner spinning={isLoading}>
            {!isImageError ? (
                readOnly ? (
                    <div className='grid min-h-[65px] w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-[15px] rounded-xl bg-(--color-body) p-[15px]'>
                        <img
                            key={requestVersion}
                            alt=''
                            className='size-[26px] shrink-0 object-contain'
                            src={imageUrl}
                            onError={handleImageError}
                            onLoad={() => setIsLoading(false)}
                        />
                        {fileDetails}
                        <div className='flex items-center justify-end gap-1'>
                            <button
                                aria-label={t('translation:preview')}
                                className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-(--color-border) bg-transparent p-0 text-(--color-card-text) shadow-xs hover:text-(--color-brand-green) focus-visible:outline-2 focus-visible:outline-(--color-brand-green)'
                                type='button'
                                onClick={() => setIsPreviewOpen(true)}
                            >
                                <Eye size={14} />
                            </button>
                            <button
                                aria-label={t('translation:download')}
                                className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-(--color-border) bg-transparent p-0 text-(--color-card-text) shadow-xs hover:text-(--color-brand-green) focus-visible:outline-2 focus-visible:outline-(--color-brand-green)'
                                type='button'
                                onClick={() => void download()}
                            >
                                <Download size={14} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <FileCardFlow
                        file={file}
                        thumbnail={
                            <img
                                key={requestVersion}
                                alt=''
                                className='size-8 shrink-0 rounded object-cover'
                                src={imageUrl}
                                onError={handleImageError}
                                onLoad={() => setIsLoading(false)}
                            />
                        }
                        onDelete={() => onDelete(id)}
                        onDownloadBlob={download}
                        onPreview={() => setIsPreviewOpen(true)}
                    />
                )
            ) : (
                <div
                    className={cn(
                        'flex min-h-[65px] items-center gap-4 rounded-lg border border-(--color-border) bg-(--color-body) p-4',
                        readOnly && 'gap-[15px] rounded-xl border-0 p-[15px]',
                    )}
                >
                    <ImageIcon
                        aria-hidden
                        className='text-(--color-card-subtitle)'
                        size={32}
                    />
                    {fileDetails}
                    {isImageError && (
                        <ShadcnButton
                            aria-label={t('translation:retry')}
                            icon={<RotateCw aria-hidden size={16} />}
                            size='sm'
                            type='button'
                            variant='outlineLk'
                            onClick={() => {
                                setIsImageError(false);
                                setIsLoading(true);
                                setRequestVersion((version) => version + 1);
                                void photo.refetch();
                            }}
                        />
                    )}
                    {!readOnly && (
                        <ShadcnButton
                            aria-label={t('translation:delete')}
                            icon={<Trash aria-hidden size={16} />}
                            size='sm'
                            type='button'
                            variant='destructiveLk'
                            popconfirm={{
                                title: t('translation:areYouSureDelete'),
                                onConfirm: () => onDelete(id),
                            }}
                        />
                    )}
                </div>
            )}
            <ShadcnModal
                destroyOnClose
                modal
                open={isPreviewOpen}
                title={file.name}
                width='calc(100vw - 32px)'
                onClose={() => setIsPreviewOpen(false)}
            >
                {imageUrl && (
                    <img
                        alt={file.name}
                        className='mx-auto max-h-[calc(100dvh-160px)] max-w-full object-contain'
                        src={imageUrl}
                    />
                )}
            </ShadcnModal>
        </ProSpinner>
    );
}
